import { allFrontendCustomWorkQSlugs } from "../components/work_queues/pages/custom/globalWorkQStructure"
import { filterActions } from "../store/filter"
import { workqueueActions } from "../store/workqueue"

/**
 * Resolve the active work-queue slug from the URL (including custom queues without :workqSlug)
 * or Redux fallback.
 */
export function resolveWorkQueueSlugFromRoute({ pathname = "", workqSlug, selectedWorkQueue } = {}) {
    if (workqSlug) {
        return workqSlug
    }

    for (const slug of allFrontendCustomWorkQSlugs) {
        if (pathname.startsWith(`/work-queues/${slug}`)) {
            return slug
        }
    }

    const match = pathname.match(/^\/work-queues\/([^/]+)/)
    if (match?.[1]) {
        return match[1]
    }

    return selectedWorkQueue?.slug ?? null
}

export function isCustomWorkQueueSlug(slug) {
    return Boolean(slug && allFrontendCustomWorkQSlugs.includes(slug))
}

function isCustomWorkQueueContext({ pathname = "", workqSlug, selectedWorkQueue } = {}) {
    if (selectedWorkQueue?.type === "custom") {
        return true
    }
    const slug = resolveWorkQueueSlugFromRoute({ pathname, workqSlug, selectedWorkQueue })
    return isCustomWorkQueueSlug(slug)
}

/**
 * Patient path from header global search.
 * Custom work queues (e.g. police-report) always open /cases/:id; standard queues stay on /work-queues/:slug/:id.
 */
export function workQueuePatientHref(personNumber, { pathname = "", workqSlug, selectedWorkQueue } = {}) {
    if (!personNumber) {
        return "/cases"
    }

    if (isCustomWorkQueueContext({ pathname, workqSlug, selectedWorkQueue })) {
        return `/cases/${personNumber}`
    }

    const onWorkQueueRoute = pathname.startsWith("/work-queues/")
    const slug = resolveWorkQueueSlugFromRoute({ pathname, workqSlug, selectedWorkQueue })

    if (onWorkQueueRoute && slug) {
        return `/work-queues/${slug}/${personNumber}`
    }

    if (selectedWorkQueue?.slug) {
        return `/work-queues/${selectedWorkQueue.slug}/${personNumber}`
    }

    return `/cases/${personNumber}`
}

/** Clears list snapshot, shows loading, resets page, closes drawer — call when switching work queues. */
export function dispatchWorkQueueNavigationState(dispatch) {
    dispatch(filterActions.setSnapshotData(null))
    dispatch(filterActions.setMasterLoading(true))
    dispatch(filterActions.setCurrentPage(1))
    dispatch(workqueueActions.setOpenedDrawer(null))
}

/**
 * Sync Redux when selecting a work queue (without navigating).
 * @param {import('redux').Dispatch} dispatch
 * @param {{ slug: string }} item — work queue row from system data
 */
export function selectWorkQueue(dispatch, item) {
    if (!item?.slug) return
    dispatchWorkQueueNavigationState(dispatch)
    dispatch(workqueueActions.setSelectedWorkQueue(item))
}

/**
 * Navigate to a work queue and sync Redux so the list refetches immediately.
 * @param {import('react-router-dom').NavigateFunction} navigate
 * @param {import('redux').Dispatch} dispatch
 * @param {{ slug: string }} item — work queue row from system data
 */
export function goToWorkQueue(navigate, dispatch, item) {
    if (!item?.slug) return
    navigate(`/work-queues/${item.slug}`)
    selectWorkQueue(dispatch, item)
}
